import { useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import type { UnitSlot, Slot } from '@/api/types';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';

export function timeToMinutes(timeLocal: string): number {
  const [hours, minutes] = timeLocal.split(':').map(Number);
  return hours * 60 + minutes;
}

export function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

export function calculatePrice(
  startTimeLocal: string,
  durationMinutes: number,
  incrementMinutes: number,
  unitSlotMap: Map<string, Slot>,
): number {
  let total = 0;
  const startMinutes = timeToMinutes(startTimeLocal);

  for (let i = 0; i < durationMinutes; i += incrementMinutes) {
    const timeKey = minutesToTime(startMinutes + i);
    const slot = unitSlotMap.get(timeKey);
    if (slot) {
      total += slot.pricePerHour * (incrementMinutes / 60);
    }
  }

  return Math.round(total * 100) / 100;
}

export function checkConsecutiveAvailability(
  startTimeLocal: string,
  durationMinutes: number,
  incrementMinutes: number,
  unitSlotMap: Map<string, Slot>,
): boolean {
  const startMinutes = timeToMinutes(startTimeLocal);

  for (let i = 0; i < durationMinutes; i += incrementMinutes) {
    const timeKey = minutesToTime(startMinutes + i);
    if (!unitSlotMap.has(timeKey)) return false;
  }

  return true;
}

/** Format time for the hour-label column: "12 AM", "1 PM", etc. */
function formatHourLabel(time: string): string {
  const [hours] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const display = hours % 12 || 12;
  return `${display} ${period}`;
}

const ROW_HEIGHT = 48; // matches min-h-[48px]

type AvailabilityGridProps = {
  units: UnitSlot[];
  selectedUnitId: string | null;
  selectedStartTime: string | null;
  selectedDuration: number;
  bookingIncrement: number;
  minDuration: number;
  maxDuration: number;
  currency: string;
  date?: string; // "YYYY-MM-DD" — used to filter past hours on today
  onSelectSlot: (unitId: string, startTime: string) => void;
  onDurationChange: (duration: number) => void;
};

export default function AvailabilityGrid({
  units,
  selectedUnitId,
  selectedStartTime,
  selectedDuration,
  bookingIncrement,
  minDuration,
  maxDuration,
  currency,
  date,
  onSelectSlot,
  onDurationChange,
}: AvailabilityGridProps) {
  const { t, i18n } = useTranslation();

  // Build sorted unique time rows from all slots across all units
  const allTimeRows = useMemo(() => {
    const timeSet = new Set<string>();
    units.forEach((u) => u.slots.forEach((s) => timeSet.add(s.startTimeLocal)));
    return Array.from(timeSet).sort();
  }, [units]);

  // Filter to relevant hours only — skip past hours on today, 1h buffer around slots
  const timeRows = useMemo(() => {
    if (allTimeRows.length === 0) return allTimeRows;

    // On today, calculate the current time as a floor to skip past rows
    let nowMinutes = 0;
    if (date) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      if (date === todayStr) {
        nowMinutes = today.getHours() * 60 + today.getMinutes();
        // Round down to nearest hour for a clean start
        nowMinutes = Math.floor(nowMinutes / 60) * 60;
      }
    }

    let firstMinutes = Infinity;
    let lastMinutes = -Infinity;

    units.forEach((unit) => {
      unit.slots.forEach((slot) => {
        const m = timeToMinutes(slot.startTimeLocal);
        if (m < firstMinutes) firstMinutes = m;
        if (m > lastMinutes) lastMinutes = m;
      });
    });

    if (firstMinutes === Infinity) return allTimeRows;

    const bufferMinutes = 60;
    // Start from max of: (first slot - buffer) or (current hour on today)
    const startMinutes = Math.max(nowMinutes, Math.max(0, firstMinutes - bufferMinutes));
    const endMinutes = Math.min(24 * 60 - 1, lastMinutes + bufferMinutes);

    return allTimeRows.filter((time) => {
      const m = timeToMinutes(time);
      return m >= startMinutes && m <= endMinutes;
    });
  }, [allTimeRows, units, date]);

  // Build lookup: unitId → startTimeLocal → Slot
  const slotMap = useMemo(() => {
    const map = new Map<string, Map<string, Slot>>();
    units.forEach((unit) => {
      const unitMap = new Map<string, Slot>();
      unit.slots.forEach((slot) => unitMap.set(slot.startTimeLocal, slot));
      map.set(unit.unitId, unitMap);
    });
    return map;
  }, [units]);

  // Calculate selected time range in minutes
  const selectedStartMinutes = selectedStartTime ? timeToMinutes(selectedStartTime) : -1;
  const selectedEndMinutes = selectedStartMinutes + selectedDuration;

  // Check if a cell is the start of the selection, within range, or the last cell
  const checkSelection = (unitId: string, time: string) => {
    if (!selectedStartTime || selectedUnitId !== unitId) {
      return { isSelected: false, isInRange: false, isLastInRange: false };
    }
    const cellMinutes = timeToMinutes(time);
    const isSelected = selectedStartTime === time;
    const isInRange =
      !isSelected &&
      cellMinutes > selectedStartMinutes &&
      cellMinutes < selectedEndMinutes;
    const isLastInRange =
      isInRange &&
      cellMinutes + bookingIncrement >= selectedEndMinutes;
    return { isSelected, isInRange, isLastInRange };
  };

  // Detect price boundaries — returns new price if price changed from previous row
  const getPriceBoundary = (rowIndex: number): number | null => {
    if (rowIndex === 0) return null;
    const prevTime = timeRows[rowIndex - 1];
    const currTime = timeRows[rowIndex];

    for (const unit of units) {
      const unitSlots = slotMap.get(unit.unitId);
      if (!unitSlots) continue;
      const prevSlot = unitSlots.get(prevTime);
      const currSlot = unitSlots.get(currTime);
      if (prevSlot && currSlot && prevSlot.pricePerHour !== currSlot.pricePerHour) {
        return currSlot.pricePerHour;
      }
    }
    return null;
  };

  // Get primary feature label for column header
  const getFeatureLabel = (features: Record<string, unknown>): string | null => {
    const entries = Object.entries(features);
    if (entries.length === 0) return null;
    return String(entries[0][1]);
  };

  // ── Drag-to-extend ──
  const dragState = useRef<{
    startY: number;
    startDuration: number;
  } | null>(null);

  const getUnitSlotMap = useCallback(() => {
    if (!selectedUnitId) return null;
    return slotMap.get(selectedUnitId) ?? null;
  }, [selectedUnitId, slotMap]);

  const clampDuration = useCallback(
    (dur: number): number => {
      const clamped = Math.max(minDuration, Math.min(maxDuration, dur));
      // Snap to increment
      return Math.round(clamped / bookingIncrement) * bookingIncrement;
    },
    [minDuration, maxDuration, bookingIncrement],
  );

  const handleDragMove = useCallback(
    (clientY: number) => {
      if (!dragState.current || !selectedStartTime) return;
      const deltaY = clientY - dragState.current.startY;
      const rowsDelta = Math.round(deltaY / ROW_HEIGHT);
      const newDuration = dragState.current.startDuration + rowsDelta * bookingIncrement;
      const clamped = clampDuration(newDuration);

      // Check consecutive availability before applying
      const unitSlots = getUnitSlotMap();
      if (unitSlots && clamped !== selectedDuration) {
        if (checkConsecutiveAvailability(selectedStartTime, clamped, bookingIncrement, unitSlots)) {
          onDurationChange(clamped);
        }
      }
    },
    [selectedStartTime, selectedDuration, bookingIncrement, clampDuration, getUnitSlotMap, onDurationChange],
  );

  const handleDragEnd = useCallback(() => {
    dragState.current = null;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);

  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      dragState.current = { startY: clientY, startDuration: selectedDuration };
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'row-resize';

      const onMove = (ev: MouseEvent | TouchEvent) => {
        const y = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
        handleDragMove(y);
      };
      const onUp = () => {
        handleDragEnd();
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('touchend', onUp);
    },
    [selectedDuration, handleDragMove, handleDragEnd],
  );

  const gridCols = `80px repeat(${units.length}, 1fr)`;

  // How many rows the selection spans
  const selectionSpan = Math.max(1, selectedDuration / bookingIncrement);

  if (units.length === 0 || timeRows.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {t('booking.no_slots')}
      </p>
    );
  }

  const isNarrow = units.length <= 1;

  return (
    <div className={cn("overflow-x-auto rounded-lg border", isNarrow && "mx-auto max-w-xl")}>
      <div className={isNarrow ? undefined : "min-w-[600px]"}>
        {/* Column headers */}
        <div
          className="grid border-b bg-muted/30"
          style={{ gridTemplateColumns: gridCols }}
        >
          <div /> {/* empty corner */}
          {units.map((unit) => {
            const isMaintenance = unit.operationalStatus === 'MAINTENANCE' || unit.operationalStatus === 'OFFLINE';
            return (
              <div key={unit.unitId} className={cn("p-3 text-center border-l min-w-[150px]", isMaintenance && "opacity-50")}>
                <p className="font-semibold text-sm">{unit.unitName}</p>
                {isMaintenance ? (
                  <span className="inline-block mt-1 rounded bg-yellow-50 px-1.5 py-0.5 text-xs text-yellow-700 border border-yellow-200">
                    {unit.operationalStatus === 'MAINTENANCE' ? t('admin.maintenance') : t('admin.offline')}
                  </span>
                ) : unit.features ? (
                  <p className="text-xs text-muted-foreground">
                    {getFeatureLabel(unit.features)}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Time rows */}
        {timeRows.map((time, rowIndex) => {
          const priceBoundary = getPriceBoundary(rowIndex);
          const minutes = timeToMinutes(time);
          const isOnTheHour = minutes % 60 === 0;

          return (
            <div key={time}>
              {/* Price boundary line */}
              {priceBoundary !== null && (
                <div
                  className="grid border-t-2 border-dashed border-muted-foreground/30 relative"
                  style={{ gridTemplateColumns: gridCols }}
                >
                  <div className="col-span-full" />
                  <div className="absolute right-3 -top-2.5 text-xs text-muted-foreground bg-background px-1.5 rounded">
                    {formatCurrency(priceBoundary, currency, i18n.language)}/h
                  </div>
                </div>
              )}

              {/* Row — hour lines on :00 rows */}
              <div className={cn("grid", isOnTheHour && rowIndex > 0 && "border-t border-muted-foreground/20")} style={{ gridTemplateColumns: gridCols }}>
                {/* Time label — only on the hour, aligned to top */}
                <div className={cn(
                  "p-2 text-xs text-muted-foreground text-right pr-3 border-r flex justify-end min-w-[60px]",
                  isOnTheHour ? "items-start pt-1" : "items-center",
                )}>
                  {isOnTheHour ? formatHourLabel(time) : null}
                </div>

                {/* Unit cells */}
                {units.map((unit) => {
                  const isMaintenance = unit.operationalStatus === 'MAINTENANCE' || unit.operationalStatus === 'OFFLINE';
                  if (isMaintenance) {
                    return (
                      <div key={unit.unitId} className="border-l border-b min-h-[48px] bg-muted/40" />
                    );
                  }
                  const slot = slotMap.get(unit.unitId)?.get(time);
                  const isAvailable = !!slot;
                  const { isSelected, isInRange, isLastInRange } =
                    checkSelection(unit.unitId, time);
                  const isPartOfSelection = isSelected || isInRange;
                  // Only the very last cell in the selection gets the drag handle
                  const isLastCell = isLastInRange || (isSelected && selectionSpan === 1);

                  return (
                    <div
                      key={unit.unitId}
                      className={cn(
                        'group relative border-l min-h-[48px] transition-colors',
                        // Border-b on all cells EXCEPT between consecutive selected cells
                        !isPartOfSelection && 'border-b',
                        isPartOfSelection && isLastCell && 'border-b',
                        // Available (unselected)
                        isAvailable && !isPartOfSelection && 'cursor-pointer hover:bg-primary/5',
                        // All selected cells: same bg-primary, no opacity difference
                        isSelected && 'bg-primary text-primary-foreground rounded-t-md',
                        isInRange && !isLastInRange && 'bg-primary text-primary-foreground',
                        isLastInRange && 'bg-primary text-primary-foreground rounded-b-md',
                        // Unavailable
                        !isAvailable &&
                          !isPartOfSelection &&
                          'bg-zinc-200 dark:bg-zinc-800 cursor-not-allowed border-b',
                      )}
                      {...(!isAvailable && !isPartOfSelection
                        ? {
                            style: {
                              backgroundImage:
                                'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(0,0,0,0.06) 4px, rgba(0,0,0,0.06) 5px)',
                            },
                          }
                        : {})}
                      onClick={() =>
                        isAvailable && onSelectSlot(unit.unitId, time)
                      }
                    >
                      {/* Available cell: ⊕ icon */}
                      {isAvailable && !isPartOfSelection && (
                        <div className="flex items-center justify-center h-full">
                          <Plus className="h-4 w-4 text-muted-foreground/20 group-hover:text-muted-foreground/60 transition-colors" />
                        </div>
                      )}

                      {/* Selected cell (first): duration label */}
                      {isSelected && selectionSpan === 1 && (
                        <div className="flex items-center justify-center h-full">
                          <span className="text-xs font-medium">
                            {t('booking.duration_minutes', { count: selectedDuration })}
                          </span>
                        </div>
                      )}
                      {isSelected && selectionSpan > 1 && (
                        <div className="absolute top-1 left-2 text-xs font-medium">
                          {t('booking.duration_minutes', { count: selectedDuration })}
                        </div>
                      )}

                      {/* Drag handle — only on the very last cell of the selection */}
                      {isLastCell && isPartOfSelection && (
                        <div
                          className="absolute bottom-0 left-0 right-0 h-3 flex items-center justify-center cursor-row-resize z-10"
                          onMouseDown={handleDragStart}
                          onTouchStart={handleDragStart}
                        >
                          <div className="w-8 h-1 rounded-full bg-primary-foreground/60" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
